import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStructuredData } from '@/hooks/useStructuredData';

const SITEMAP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Sitemap — Onetvservices',
  url: 'https://onetvservices.com/sitemap',
  description: 'Complete sitemap of Onetvservices — all pages including pricing, legal, about, and support.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://onetvservices.com' },
      { '@type': 'ListItem', position: 2, name: 'Sitemap', item: 'https://onetvservices.com/sitemap' },
    ],
  },
};

const PAGES = [
  {
    category: 'Main Pages',
    icon: 'ri-home-4-line',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10 border-sky-500/20',
    links: [
      { label: 'Home', path: '/', desc: 'Premium IPTV subscriptions — +25,000 channels, HD & 4K', updated: '2026-04-03' },
      { label: 'About Us', path: '/about', desc: 'Business identity, team, values, and company history', updated: '2026-04-03' },
    ],
  },
  {
    category: 'Legal Pages',
    icon: 'ri-scales-line',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/20',
    links: [
      { label: 'Terms & Conditions', path: '/terms', desc: 'Service rules, acceptable use, subscription terms', updated: '2026-04-03' },
      { label: 'Privacy Policy', path: '/privacy', desc: 'Data collection, GDPR rights, cookie policy', updated: '2026-04-03' },
      { label: 'Refund Policy', path: '/refund', desc: '7-day money-back guarantee and refund process', updated: '2026-04-03' },
    ],
  },
  {
    category: 'This Page',
    icon: 'ri-map-2-line',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
    links: [
      { label: 'Sitemap', path: '/sitemap', desc: 'Full index of all pages on onetvservices.com', updated: '2026-04-03' },
    ],
  },
];

export default function SitemapPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useStructuredData(SITEMAP_SCHEMA);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-white/10 py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg" />
            <span className="text-sm">Back to Home</span>
          </Link>
          <div className="w-px h-5 bg-white/20" />
          <Link to="/">
            <img
              src="https://storage.readdy-site.link/project_files/ea087323-c332-4d1a-a768-642201e38806/8e03886a-a466-4209-8fc0-30625673693c_ONETV.png?v=410354e2b00bf17cf48639a5651635ec"
              alt="Onetvservices Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-gray-400 text-sm font-medium mb-6">
            <i className="ri-map-2-line" />
            Site Index
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Sitemap</h1>
          <p className="text-white/50 text-sm">Complete index of all pages on onetvservices.com — last updated April 3, 2026</p>
        </div>

        <div className="space-y-8">
          {PAGES.map(({ category, icon, color, bg, links }) => (
            <div key={category}>
              <div className={`inline-flex items-center gap-2 border ${bg} rounded-full px-3 py-1 text-xs font-semibold mb-4 ${color}`}>
                <i className={icon} />
                {category}
              </div>
              <div className="space-y-3">
                {links.map(({ label, path, desc, updated }) => (
                  <Link
                    key={path}
                    to={path}
                    className="flex items-center justify-between bg-white/4 hover:bg-white/8 border border-white/8 rounded-xl px-5 py-4 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${bg} flex-shrink-0 mt-0.5`}>
                        <i className={`${icon} ${color} text-sm`} />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm group-hover:text-sky-300 transition-colors">{label}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
                        <p className="text-gray-600 text-[11px] mt-1">
                          <span className="font-mono">onetvservices.com{path}</span>
                          <span className="ml-3">Updated: {updated}</span>
                        </p>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-gray-600 group-hover:text-sky-400 transition-colors flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Google indexing note */}
        <div className="mt-12 bg-white/4 border border-white/8 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-white/8 rounded-xl flex-shrink-0">
              <i className="ri-google-line text-white/60 text-xl" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-1">For Search Engines</p>
              <p className="text-gray-500 text-xs leading-relaxed">
                All pages on this site include structured data markup (Schema.org JSON-LD) to help Google and other search engines understand the content. Each page has breadcrumb navigation, page type, and publisher information embedded for optimal indexing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8 px-4 text-center text-white/40 text-sm">
        <p>Onetvservices 2019-2026 © All Rights Reserved</p>
        <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
          <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
          <span>·</span>
          <Link to="/about" className="hover:text-white/70 transition-colors cursor-pointer">About Us</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-white/70 transition-colors cursor-pointer">Terms</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-white/70 transition-colors cursor-pointer">Privacy</Link>
          <span>·</span>
          <Link to="/refund" className="hover:text-white/70 transition-colors cursor-pointer">Refund</Link>
          <span>·</span>
          <Link to="/sitemap" className="text-sky-400 cursor-pointer">Sitemap</Link>
        </div>
      </div>
    </div>
  );
}
